import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import {
  calcTotals,
  DOC_LABEL,
  STANDARD_TEXTS,
  TEXT_KIND_LABEL,
  type CartLine,
  type TextKind,
  type TextTemplateInfo,
  DOC_PLURAL,
  DUNNING_LABEL,
  type DocumentShareCreated,
  type DunningDueItem,
  type DunningSettings,
  formatCents,
  INCOTERMS,
  INCOTERM_KEYS,
  isFreeDoc,
  PAYMENT_TERM_PRESETS,
  type Incoterm,
  lineNetCents,
  STATE_LABEL,
  type CustomerInfo,
  type DocLineInput,
  type DocType,
  type DocumentDetail,
  type DocumentListResponse,
  type DocumentSummary,
  type ItemInfo,
  type MeResponse,
  type NumberingInfo,
  invoicesFromCsv,
  type CsvInvoiceResult,
  type InvoiceImportResult,
  VAT_REASON_KEYS,
  VAT_REASONS,
  type VatReason,
} from "@handvero/shared";
import { api, errorText, upload } from "../api";
import { eurosToCents } from "./Customers";
import { ProjectFilePicker } from "./ProjectFilePicker";
import { CartImportSheet } from "./CartImport";
import { DictationSheet } from "./Dictation";
import { Icon, Message, PdfViewer, Sheet, useUnauthorizedRedirect } from "./ui";
import "./customers.css";
import "./documents.css";

/*
 * Angebote, Aufträge und Rechnungen (nur am PC, pro Firma freigeschaltet).
 * Entwurf frei bearbeiten, „Festschreiben“ vergibt die Nummer und sperrt den Beleg.
 */

const PAY_PRESETS: [number, string][] = [[0, "Sofort"], [7, "7 Tage"], [14, "14 Tage"], [30, "30 Tage"], [60, "60 Tage"]];
const VALID_PRESETS: [number, string][] = [[7, "7 Tage"], [14, "14 Tage"], [30, "30 Tage"], [60, "60 Tage"], [90, "90 Tage"]];

/** Datum plus Tage als JJJJ-MM-TT (mittags gerechnet, damit die Zeitumstellung nichts verschiebt). */
function addDaysText(day: string, n: number): string {
  const d = new Date(`${day || new Date().toISOString().slice(0, 10)}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

type Tab = "invoice" | "quote" | "order" | "numbers" | "dunning";

const centsToText = (c: number) => (c / 100).toFixed(2).replace(".", ",");
const dateText = (d: string | null) => (d ? d.split("-").reverse().join(".") : "");

/** Adresse in der Seitenleiste → Belegart. */
export const DOC_TABS: Record<string, Tab> = { rechnungen: "invoice", angebote: "quote", auftraege: "order", nummern: "numbers", einstellungen: "numbers", mahnungen: "dunning" };

export function DocumentsView({ me, tab: tabProp }: { me: MeResponse; tab?: Tab }) {
  const tab = tabProp ?? "invoice";
  const [openId, setOpenId] = useState<string | null>(null);
  const manager = me.user.role !== "employee";
  if (!manager) return <p className="cu-empty">Angebote und Rechnungen sehen nur Inhaber und Admins.</p>;
  if (openId) return <DocEditor id={openId} onBack={() => setOpenId(null)} onOpen={setOpenId} />;
  return (
    <div className="cu">
      {tab === "numbers" ? (
        <>
          <p className="muted-s">
            Firmenangaben, Zahlungsziel, Farbe, Logo und QR-Code für Rechnungen stellen Sie unter <a href="/desktop/firma">Firmenangaben</a> ein. Hier legen Sie die Nummern Ihrer Belege fest.
          </p>
          <NumberingForm />
        </>
      ) : tab === "dunning" ? <DunningView onOpen={setOpenId} /> : <DocList key={tab} type={tab} onOpen={setOpenId} />}
    </div>
  );
}

// ---------- Liste ----------

function DocList({ type, onOpen }: { type: DocType; onOpen: (id: string) => void }) {
  const onAuth = useUnauthorizedRedirect();
  const [data, setData] = useState<DocumentListResponse | null>(null);
  const [query, setQuery] = useState("");
  const [state, setState] = useState("");
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [dictating, setDictating] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = useCallback(() => {
    const p = new URLSearchParams({ type });
    if (query.trim()) p.set("q", query.trim());
    if (state) p.set("state", state);
    api
      .get<DocumentListResponse>(`/api/documents?${p}`)
      .then((d) => {
        setData(d);
        setError("");
      })
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [type, query, state, onAuth]);
  useEffect(() => {
    const timer = setTimeout(load, query ? 250 : 0);
    return () => clearTimeout(timer);
  }, [load, query]);

  const states = type === "invoice" ? (["draft", "open", "overdue", "paid", "cancelled"] as const) : (["open"] as const);
  return (
    <section className="cu-panel">
      {type === "invoice" && data && (
        <div className="dc-sums">
          <div>
            <small>Offen</small>
            <b>{formatCents(data.openCents)}</b>
          </div>
          <div className={data.overdueCents > 0 ? "warn" : undefined}>
            <small>Fällig</small>
            <b>{formatCents(data.overdueCents)}</b>
          </div>
        </div>
      )}
      <div className="cu-bar">
        <div className="cu-roles" role="tablist">
          <button className={state === "" ? "on" : undefined} onClick={() => setState("")}>
            Alle
          </button>
          {states.map((s) => (
            <button key={s} className={state === s ? "on" : undefined} onClick={() => setState(s)}>
              {type !== "invoice" && s === "open" ? "Offen" : STATE_LABEL[s]}
            </button>
          ))}
        </div>
        <label className="dsearch cu-search">
          <Icon name="search" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Nummer, Kunde, Betreff …" aria-label="Belege suchen" />
        </label>
        {type === "invoice" && (
          <button className="dbtn ghost" onClick={() => setImporting(true)}>
            Rechnungen übernehmen
          </button>
        )}
        {type !== "credit_note" && (
          <button className="dbtn ghost" onClick={() => setDictating(true)}>
            <Icon name="mic" /> Per Sprache oder Text
          </button>
        )}
        <button className="dbtn" onClick={() => setCreating(true)}>
          <Icon name="plus" /> {DOC_LABEL[type]} erstellen
        </button>
      </div>
      <Message>{error}</Message>
      {importing && <InvoiceImportSheet onClose={() => setImporting(false)} onDone={() => { setImporting(false); load(); }} />}
      {data === null && !error && <p className="muted-s">Lädt …</p>}
      {data?.items.length === 0 && <p className="cu-empty">Noch kein Eintrag. Erstellen Sie {type === "quote" ? "Ihr erstes Angebot" : type === "order" ? "Ihren ersten Auftrag" : "Ihre erste Rechnung"}.</p>}
      {data && data.items.length > 0 && (
        <table className="cu-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Nummer</th>
              <th>Kunde</th>
              <th>Datum</th>
              <th>{type === "invoice" ? "Fällig" : "Gültig bis"}</th>
              <th className="r">Betrag</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((d) => (
              <DocRow key={d.id} d={d} onOpen={onOpen} />
            ))}
          </tbody>
        </table>
      )}
      {dictating && (
        <DictationSheet
          onClose={() => setDictating(false)}
          onCreated={(id) => {
            setDictating(false);
            onOpen(id);
          }}
        />
      )}
      {creating && (
        <NewDocSheet
          type={type}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            onOpen(id);
          }}
        />
      )}
    </section>
  );
}

function DocRow({ d, onOpen, showType }: { d: DocumentSummary; onOpen: (id: string) => void; showType?: boolean }) {
  const label = d.type !== "invoice" && d.state === "open" ? (d.status === "final" ? "Festgeschrieben" : "Offen") : STATE_LABEL[d.state];
  return (
    <tr onClick={() => onOpen(d.id)} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen(d.id)}>
      <td>
        <span className={`dc-state ${d.state}`}>
          {d.status === "final" && <Icon name="lock" />}
          {label}
        </span>
        {d.sentOn && <small className="cu-role">versendet am {dateText(d.sentOn)}</small>}
      </td>
      <td className="mono">
        {d.number ?? "–"}
        {d.attachmentCount > 0 && (
          <span title={`${d.attachmentCount} Datei(en) angehängt`} aria-label={`${d.attachmentCount} Datei(en) angehängt`} style={{ marginLeft: 6, display: "inline-flex", verticalAlign: "middle" }}>
            <Icon name="clip" />
          </span>
        )}
      </td>
      <td>
        {d.customerName}
        <small className="cu-role">{showType ? `${DOC_LABEL[d.type]}${d.subject ? ` · ${d.subject}` : ""}` : d.subject}</small>
      </td>
      <td>{dateText(d.issueDate)}</td>
      <td>{dateText(d.dueDate)}</td>
      <td className="r mono">{formatCents(d.grossCents)}</td>
    </tr>
  );
}

function NewDocSheet(props: { type: DocType; onClose: () => void; onCreated: (id: string) => void }) {
  const [customers, setCustomers] = useState<CustomerInfo[] | null>(null);
  const [customerId, setCustomerId] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .get<CustomerInfo[]>("/api/customers?role=customer")
      .then(setCustomers)
      .catch((err) => setError(errorText(err)));
  }, []);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const d = await api.post<DocumentDetail>("/api/documents", { type: props.type, customerId });
      props.onCreated(d.id);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }
  return (
    <Sheet title={`${DOC_LABEL[props.type]} erstellen`} onClose={props.onClose}>
      <form className="cu-form" onSubmit={submit}>
        <p className="muted-s wide">Jeder Beleg gehört zu einem Kunden. Fehlt der Kunde noch, legen Sie ihn zuerst unter „Kunden“ an.</p>
        <label className="wide">
          Kunde
          <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} required autoFocus>
            <option value="">Bitte wählen …</option>
            {customers?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.number} · {c.name}
                {c.city ? `, ${c.city}` : ""}
              </option>
            ))}
          </select>
        </label>
        <div className="wide">
          <Message>{error}</Message>
        </div>
        <div className="cu-buttons wide">
          <button type="button" className="dbtn ghost" onClick={props.onClose}>
            Abbrechen
          </button>
          <button className="dbtn" disabled={!customerId || busy}>
            Weiter
          </button>
        </div>
      </form>
    </Sheet>
  );
}

// ---------- Editor ----------

interface LineForm {
  name: string;
  description: string;
  quantity: string;
  unit: string;
  price: string;
  vatRate: 0 | 7 | 19;
  discount: string;
  alt: boolean;
  markup: string;
  hide: boolean;
}

const toForm = (l: DocumentDetail["lines"][number]): LineForm => ({
  name: l.name,
  description: l.description ?? "",
  quantity: String(l.quantity).replace(".", ","),
  unit: l.unit,
  price: centsToText(l.unitPriceCents),
  vatRate: l.vatRate,
  discount: String(l.discountPercent).replace(".", ","),
  alt: l.isAlternative,
  markup: String(l.markupPercent).replace(".", ","),
  hide: l.hideAdjustment,
});

function parseNumber(t: string): number | null {
  const n = Number(t.replace(/\./g, "").replace(",", "."));
  return t.trim() !== "" && Number.isFinite(n) ? n : null;
}

function lineInput(l: LineForm): DocLineInput | null {
  const quantity = parseNumber(l.quantity);
  const discount = parseNumber(l.discount || "0");
  const markup = parseNumber(l.markup || "0");
  const neg = l.price.trim().startsWith("-");
  const cents = eurosToCents(l.price.replace(/^-/, ""));
  if (quantity === null || discount === null || markup === null || cents === null) return null;
  return {
    name: l.name,
    description: l.description.trim() || null,
    quantity,
    unit: l.unit || "Stk",
    unitPriceCents: neg ? -cents : cents,
    vatRate: l.vatRate,
    discountPercent: discount,
    isAlternative: l.alt,
    markupPercent: markup,
    hideAdjustment: l.hide,
  };
}

/** Auswahl fertiger und eigener Texte für Betreff, Einleitung und Schlusstext. */
function TextPicker(props: { kind: TextKind; type: DocType; templates: TextTemplateInfo[]; value: string; onPick: (t: string) => void; onChanged: () => void }) {
  const [sel, setSel] = useState("");
  const [err, setErr] = useState("");
  const standard = STANDARD_TEXTS[props.kind].filter((t) => !t.types || t.types.includes(props.type));
  const own = props.templates.filter((t) => t.kind === props.kind);
  const chosenOwn = own.find((t) => `own:${t.id}` === sel);
  function pick(v: string) {
    setSel(v);
    setErr("");
    const text = v.startsWith("std:") ? standard[Number(v.slice(4))]?.body : own.find((t) => `own:${t.id}` === v)?.body;
    if (text !== undefined) props.onPick(text);
  }
  async function save() {
    const name = window.prompt(`Wie soll dieser Text heißen? (zum Beispiel „${TEXT_KIND_LABEL[props.kind]} Standard“)`);
    if (!name?.trim()) return;
    try {
      await api.post("/api/billing/text-templates", { kind: props.kind, name, body: props.value });
      props.onChanged();
      setErr("");
    } catch (e) {
      setErr(errorText(e));
    }
  }
  async function remove() {
    if (!chosenOwn || !window.confirm(`Die Vorlage „${chosenOwn.name}“ löschen? Bereits geschriebene Belege bleiben unverändert.`)) return;
    try {
      await api.del(`/api/billing/text-templates/${chosenOwn.id}`);
      setSel("");
      props.onChanged();
    } catch (e) {
      setErr(errorText(e));
    }
  }
  return (
    <div className="cu-actions wide">
      <select className="dc-pick" value={sel} onChange={(e) => pick(e.target.value)} aria-label={`${TEXT_KIND_LABEL[props.kind]} aus Vorlage wählen`}>
        <option value="">{TEXT_KIND_LABEL[props.kind]} aus Vorlage wählen …</option>
        <optgroup label="Fertige Texte">
          {standard.map((t, i) => (
            <option key={t.name} value={`std:${i}`}>
              {t.name}
            </option>
          ))}
        </optgroup>
        {own.length > 0 && (
          <optgroup label="Eigene Texte">
            {own.map((t) => (
              <option key={t.id} value={`own:${t.id}`}>
                {t.name}
              </option>
            ))}
          </optgroup>
        )}
      </select>
      <button type="button" className="cu-link" onClick={() => void save()} disabled={!props.value.trim()}>
        Diesen Text als Vorlage speichern
      </button>
      {chosenOwn && (
        <button type="button" className="cu-link" onClick={() => void remove()}>
          Vorlage löschen
        </button>
      )}
      {err && <span className="muted-s">{err}</span>}
    </div>
  );
}

/** Belege eines Projekts: Angebote, Aufträge und Rechnungen direkt im Projekt anlegen und öffnen. */
export function ProjectDocuments({ projectId, customerId }: { projectId: string; customerId: string | null }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [data, setData] = useState<DocumentSummary[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    api
      .get<DocumentListResponse>(`/api/documents?projectId=${projectId}`)
      .then((r) => setData(r.items))
      .catch((err) => setError(errorText(err)));
  }, [projectId]);
  useEffect(() => {
    if (!openId) load();
  }, [load, openId]);

  async function create(type: DocType) {
    if (!customerId) {
      setError("Dieses Projekt hat noch keinen Kunden. Wählen Sie ihn über die drei Punkte oben beim Projekt aus, dann können Sie hier Belege anlegen.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const d = await api.post<DocumentDetail>("/api/documents", { type, customerId, projectId });
      setOpenId(d.id);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (openId) return <DocEditor id={openId} onBack={() => setOpenId(null)} onOpen={setOpenId} />;
  return (
    <div className="cu">
      <div className="cu-buttons">
        {(["quote", "order", "invoice"] as const).map((t) => (
          <button key={t} className={t === "quote" ? "dbtn" : "dbtn ghost"} disabled={busy} onClick={() => void create(t)}>
            <Icon name="plus" /> {DOC_LABEL[t]} erstellen
          </button>
        ))}
      </div>
      {error && <p role="alert">{error}</p>}
      {data && data.length === 0 && <p className="cu-empty">In diesem Projekt gibt es noch keine Belege. Legen Sie oben ein Angebot an.</p>}
      {data && data.length > 0 && (
        <table className="cu-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Nummer</th>
              <th>Kunde</th>
              <th>Datum</th>
              <th>Fällig / gültig bis</th>
              <th className="r">Betrag</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d) => (
              <DocRow key={d.id} d={d} onOpen={setOpenId} showType />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DocEditor(props: { id: string; onBack: () => void; onOpen: (id: string) => void }) {
  const onAuth = useUnauthorizedRedirect();
  const [doc, setDoc] = useState<DocumentDetail | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);
  const [sending, setSending] = useState(false);
  const [items, setItems] = useState<ItemInfo[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const [attBusy, setAttBusy] = useState(false);
  const [templates, setTemplates] = useState<TextTemplateInfo[]>([]);
  const reloadTemplates = useCallback(() => {
    api.get<TextTemplateInfo[]>("/api/billing/text-templates").then(setTemplates).catch(() => undefined);
  }, []);

  const [issueDate, setIssueDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [serviceFrom, setServiceFrom] = useState("");
  const [serviceTo, setServiceTo] = useState("");
  const [subject, setSubject] = useState("");
  const [intro, setIntro] = useState("");
  const [closing, setClosing] = useState("");
  const [reference, setReference] = useState("");
  const [vatReason, setVatReason] = useState<VatReason | "">("");
  const [propertyNotice, setPropertyNotice] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [variantLabel, setVariantLabel] = useState("");
  const [incoterm, setIncoterm] = useState<Incoterm | "">("");
  const [deliveryNote, setDeliveryNote] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [lines, setLines] = useState<LineForm[]>([]);

  const apply = useCallback((d: DocumentDetail) => {
    setDoc(d);
    setIssueDate(d.issueDate);
    setDueDate(d.dueDate ?? "");
    setServiceFrom(d.serviceFrom ?? "");
    setServiceTo(d.serviceTo ?? "");
    setSubject(d.subject ?? "");
    setIntro(d.intro ?? "");
    setClosing(d.closing ?? "");
    setReference(d.reference ?? "");
    setVatReason(d.vatReason ?? "");
    setPropertyNotice(d.propertyNotice ?? !d.buyer?.vatId);
    setVariantLabel(d.variantLabel ?? "");
    setIncoterm(d.incoterm ?? "");
    setDeliveryNote(d.deliveryNote ?? "");
    setPaymentTerms(d.paymentTerms ?? "");
    setLines(d.lines.map(toForm));
  }, []);

  useEffect(() => {
    api
      .get<DocumentDetail>(`/api/documents/${props.id}`)
      .then(apply)
      .catch((err) => onAuth(err) || setError(errorText(err)));
    api.get<ItemInfo[]>("/api/items").then(setItems).catch(() => undefined);
    reloadTemplates();
  }, [props.id, apply, onAuth, reloadTemplates]);

  const parsed = useMemo(() => lines.map(lineInput), [lines]);
  const totals = useMemo(
    () => calcTotals(parsed.filter((l): l is DocLineInput => !!l), doc?.smallBusiness ?? false),
    [parsed, doc?.smallBusiness],
  );

  if (!doc) return <section className="cu-panel">{error ? <Message>{error}</Message> : <p className="muted-s">Lädt …</p>}</section>;
  const draft = doc.status === "draft";
  const label = DOC_LABEL[doc.type];

  function setLine(i: number, patch: Partial<LineForm>) {
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  }

  function addItem(id: string) {
    const it = items.find((x) => x.id === id);
    const line: LineForm = it
      ? { name: it.name, description: it.description ?? "", quantity: "1", unit: it.unit, price: centsToText(it.priceCents), vatRate: it.vatRate as 0 | 7 | 19, discount: "0", alt: false, markup: "0", hide: false }
      : { name: "", description: "", quantity: "1", unit: "Stk", price: "0,00", vatRate: 19, discount: "0", alt: false, markup: "0", hide: false };
    setLines((ls) => [...ls, line]);
  }

  async function attach(files: File[]) {
    setAttBusy(true);
    setError("");
    try {
      let last: DocumentDetail | null = null;
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        last = await upload<DocumentDetail>(`/api/documents/${doc!.id}/attachments`, fd);
      }
      // Nur die Anhänge übernehmen, damit ungespeicherte Änderungen im Beleg erhalten bleiben.
      if (last) setDoc((d) => (d && last ? { ...d, attachments: last.attachments, log: last.log, attachmentCount: last.attachmentCount } : d));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setAttBusy(false);
    }
  }

  async function removeAttachment(aid: string) {
    if (!window.confirm("Diesen Anhang entfernen?")) return;
    try {
      const d = await api.del<DocumentDetail>(`/api/documents/${doc!.id}/attachments/${aid}`);
      setDoc((cur) => (cur ? { ...cur, attachments: d.attachments, log: d.log } : cur));
    } catch (err) {
      setError(errorText(err));
    }
  }

  function applyCart(cart: CartLine[], file: File | null) {
    setLines((ls) => [...ls, ...cart.map((c) => ({ name: c.name, description: "", quantity: String(c.quantity).replace(".", ","), unit: "Stk", price: centsToText(c.unitPriceCents), vatRate: c.vatRate, discount: "0", alt: false, markup: "0", hide: false }))]);
    setCartOpen(false);
    setNotice(`${cart.length} Position(en) aus dem Warenkorb übernommen. Bitte speichern Sie den Beleg.`);
    if (file) void attach([file]);
  }

  async function save(): Promise<DocumentDetail | null> {
    setError("");
    const inputs = parsed.map((p, i) => p ?? i);
    const bad = inputs.find((p) => typeof p === "number");
    if (bad !== undefined) {
      setError(`Bitte prüfen Sie Position ${Number(bad) + 1}: Menge, Preis, Rabatt und Aufschlag müssen Zahlen sein.`);
      return null;
    }
    try {
      const saved = await api.put<DocumentDetail>(`/api/documents/${doc!.id}`, {
        issueDate,
        dueDate: dueDate || null,
        serviceFrom: serviceFrom || null,
        serviceTo: serviceTo || null,
        subject,
        intro,
        closing,
        reference,
        vatReason: vatReason || null,
        propertyNotice,
        incoterm: incoterm || null,
        deliveryNote,
        paymentTerms,
        ...(doc!.variantGroup ? { variantLabel } : {}),
        lines: parsed,
      });
      apply(saved);
      return saved;
    } catch (err) {
      setError(errorText(err));
      return null;
    }
  }

  async function onEInvoice() {
    if (!doc) return;
    await run(async () => {
      const res = await fetch(`/api/documents/${doc.id}/xrechnung`, { credentials: "same-origin" });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string; message?: string } | null;
        throw new Error(body?.message ?? body?.error ?? "Die E-Rechnung konnte nicht erzeugt werden.");
      }
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc.number ?? "Rechnung"}_xrechnung.xml`;
      a.click();
      URL.revokeObjectURL(url);
    }, "Die E-Rechnung wurde heruntergeladen.");
  }

  async function run(fn: () => Promise<unknown>, done?: string) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await fn();
      if (done) setNotice(done);
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  const onSave = () => run(async () => void (await save()), "Gespeichert.");
  const onFinalize = () =>
    run(async () => {
      if (!window.confirm(`${label} festschreiben? Danach vergibt HandVero die Nummer und der Beleg lässt sich nicht mehr ändern.`)) return;
      if (!(await save())) return;
      const final = await api.post<DocumentDetail>(`/api/documents/${doc.id}/finalize`);
      apply(final);
      setNotice(`${label} ${final.number} ist festgeschrieben.`);
    });
  const onDelete = () =>
    run(async () => {
      if (!window.confirm("Diesen Entwurf löschen?")) return;
      await api.del(`/api/documents/${doc.id}`);
      props.onBack();
    });
  const onCancel = () =>
    run(async () => {
      if (!window.confirm(`Rechnung ${doc.number} stornieren? Dafür wird eine Gutschrift erstellt, die Rechnung selbst bleibt unverändert erhalten.`)) return;
      const credit = await api.post<DocumentDetail>(`/api/documents/${doc.id}/cancel`);
      props.onOpen(credit.id);
    });
  const onVariant = () =>
    run(async () => {
      if (draft && !(await save())) return;
      const v = await api.post<DocumentDetail>(`/api/documents/${doc.id}/variant`);
      props.onOpen(v.id);
    });
  const onConvert = (to: "order" | "invoice") =>
    run(async () => {
      if (draft && !(await save())) return;
      const n = await api.post<DocumentDetail>(`/api/documents/${doc.id}/convert`, { to });
      props.onOpen(n.id);
    });

  const free = isFreeDoc(doc.type);
  const canConvertOrder = doc.type === "quote" && (free || !draft);
  const canConvertInvoice = (doc.type === "quote" || doc.type === "order") && (free || !draft);

  return (
    <section className="cu-panel dc-editor">
      {pdfOpen && <PdfViewer url={`/api/documents/${doc.id}/pdf`} title={`${label} ${doc.number ?? "(Vorschau)"}`} onClose={() => setPdfOpen(false)} />}
      <div className="cu-bar">
        <button className="dbtn ghost" onClick={props.onBack}>
          ← Zurück
        </button>
        <div className="cu-actions">
          <button
            className="dbtn ghost"
            onClick={() =>
              void run(async () => {
                // Die Vorschau zeigt den gespeicherten Stand: erst speichern, damit alle Änderungen drin sind.
                if (draft && !(await save())) return;
                setPdfOpen(true);
              })
            }
            disabled={busy}
          >
            <Icon name="file" /> {draft && !free ? "Vorschau als PDF" : "PDF ansehen"}
          </button>
          {!draft && (doc.type === "invoice" || doc.type === "credit_note") && (
            <button className="dbtn ghost" onClick={() => void onEInvoice()} disabled={busy} title="XRechnung als XML-Datei, zum Einlesen in andere Programme">
              E-Rechnung (XML)
            </button>
          )}
          {draft ? (
            <>
              <button className="dbtn ghost danger" onClick={() => void onDelete()} disabled={busy}>
                Löschen
              </button>
              <button className="dbtn ghost" onClick={() => void onSave()} disabled={busy}>
                Speichern
              </button>
              {free ? (
                <>
                  <button className="dbtn" onClick={() => void (async () => { if (await save()) setSending(true); })()} disabled={busy}>
                    <Icon name="send" /> Verschicken
                  </button>
                  {doc.type === "quote" && (
                    <button className="dbtn ghost" onClick={() => void onVariant()} disabled={busy} title="Kopie dieses Angebots, die Sie anders gestalten können (zum Beispiel Variante B mit anderem Material)">
                      Variante anlegen
                    </button>
                  )}
                  {canConvertOrder && (
                    <button className="dbtn ghost" onClick={() => void onConvert("order")} disabled={busy}>
                      In Auftrag umwandeln
                    </button>
                  )}
                  {canConvertInvoice && (
                    <button className="dbtn ghost" onClick={() => void onConvert("invoice")} disabled={busy}>
                      In Rechnung umwandeln
                    </button>
                  )}
                </>
              ) : (
                <button className="dbtn" onClick={() => void onFinalize()} disabled={busy}>
                  <Icon name="lock" /> Festschreiben
                </button>
              )}
            </>
          ) : (
            <>
              <button className="dbtn" onClick={() => setSending(true)} disabled={busy}>
                <Icon name="send" /> Verschicken
              </button>
              {canConvertOrder && (
                <button className="dbtn ghost" onClick={() => void onConvert("order")} disabled={busy}>
                  Auftrag erstellen
                </button>
              )}
              {canConvertInvoice && (
                <button className="dbtn ghost" onClick={() => void onConvert("invoice")} disabled={busy}>
                  Rechnung erstellen
                </button>
              )}
              {doc.type === "invoice" && !doc.cancelledBy && (
                <>
                  <button className="dbtn ghost" onClick={() => setPaying(true)} disabled={busy}>
                    Zahlung eintragen
                  </button>
                  {doc.paidCents < doc.grossCents && doc.dunnings.length < 3 && (
                    <button
                      className="dbtn ghost"
                      onClick={() =>
                        void run(async () => {
                          const next = doc.dunnings.length + 1;
                          if (!window.confirm(`${DUNNING_LABEL[next]} zu ${doc.number} erstellen?`)) return;
                          apply(await api.post<DocumentDetail>(`/api/documents/${doc.id}/dunnings`));
                        })
                      }
                      disabled={busy}
                    >
                      {DUNNING_LABEL[doc.dunnings.length + 1]} erstellen
                    </button>
                  )}
                  <button className="dbtn ghost danger" onClick={() => void onCancel()} disabled={busy}>
                    Stornieren
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {doc.type !== "credit_note" && (
        <div className="dc-marks">
          <label>
            <input
              type="checkbox"
              checked={!!doc.sentOn}
              disabled={busy}
              onChange={(e) =>
                void run(async () => apply(await api.put<DocumentDetail>(`/api/documents/${doc.id}/marks`, { sentOn: e.target.checked ? new Date().toISOString().slice(0, 10) : null })))
              }
            />{" "}
            {label} versendet am
          </label>
          {doc.sentOn && (
            <input
              type="date"
              value={doc.sentOn}
              disabled={busy}
              onChange={(e) => e.target.value && void run(async () => apply(await api.put<DocumentDetail>(`/api/documents/${doc.id}/marks`, { sentOn: e.target.value })))}
            />
          )}
          {doc.type === "invoice" && !draft && !doc.cancelledBy && doc.paidCents < doc.grossCents && (
            <button
              className="dbtn ghost"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  apply(
                    await api.post<DocumentDetail>(`/api/documents/${doc.id}/payments`, {
                      amountCents: doc.grossCents - doc.paidCents,
                      paidOn: new Date().toISOString().slice(0, 10),
                      method: "Überweisung",
                      note: "Als bezahlt vermerkt",
                    }),
                  );
                }, "Als bezahlt vermerkt.")
              }
            >
              Rechnung ist bezahlt (heute)
            </button>
          )}
        </div>
      )}
      <h2 className="dc-title">
        {label} {doc.number ?? <span className="muted-s">(Entwurf)</span>}
        <span className={`dc-state ${doc.state}`}>{doc.type !== "invoice" && doc.state === "open" ? (doc.status === "final" ? "Festgeschrieben" : "Offen") : STATE_LABEL[doc.state]}</span>
      </h2>
      {doc.type === "quote" && doc.variants.length > 1 && (
        <p className="dc-variants">
          Varianten:{" "}
          {doc.variants.map((v) => (
            <button key={v.id} className={`dbtn ghost${v.id === doc.id ? " on" : ""}`} onClick={() => v.id !== doc.id && props.onOpen(v.id)} disabled={busy}>
              {v.label ?? v.number ?? "Variante"} · {formatCents(v.grossCents)}
            </button>
          ))}
        </p>
      )}
      {doc.type === "quote" && doc.variantGroup && draft && (
        <label>
          Name dieser Variante
          <input value={variantLabel} onChange={(e) => setVariantLabel(e.target.value)} maxLength={60} placeholder="Variante A" />
        </label>
      )}
      {!free && draft && <p className="muted-s">Die {label}snummer vergibt HandVero automatisch beim Festschreiben, lückenlos und stetig um 1 steigend. Mit „Vorschau als PDF“ sehen Sie das Dokument vorher, ohne dass eine Nummer verbraucht wird.</p>}
      {free && draft && <p className="muted-s">Dieses {label === "Angebot" ? "Angebot" : label} bleibt jederzeit änderbar. Festgeschrieben werden nur Rechnungen.</p>}
      <p className="muted-s">
        Kunde: <b>{doc.customerName}</b> (Nr. {doc.customerNumber})
        {doc.relatedNumber && <> · gehört zu {doc.relatedNumber}</>}
        {doc.cancelledBy && <> · storniert mit {doc.cancelledBy}</>}
      </p>
      <Message>{error}</Message>
      {notice && <p className="app-msg info">{notice}</p>}
      {!draft && (
        <p className="dc-lock">
          <Icon name="lock" /> Dieser Beleg ist festgeschrieben und wird bis {dateText(doc.retentionUntil)} aufbewahrt. Änderungen sind nur über eine Stornierung möglich.
        </p>
      )}

      <div className="cu-form">
        <label>
          Datum
          <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} disabled={!draft} required />
        </label>
        <label>
          {doc.type === "invoice" ? "Zahlbar bis" : "Gültig bis"}
          <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} disabled={!draft} />
          {draft && (
            <span className="dc-presets" aria-label="Vorauswahl">
              {(doc.type === "invoice" ? PAY_PRESETS : VALID_PRESETS).map(([n, text]) => (
                <button type="button" key={n} className={dueDate === addDaysText(issueDate, n) ? "on" : undefined} onClick={() => setDueDate(addDaysText(issueDate, n))}>
                  {text}
                </button>
              ))}
            </span>
          )}
        </label>
        {doc.type !== "quote" && (
          <>
            <label>
              Leistungsdatum oder Zeitraum von
              <input type="date" value={serviceFrom} onChange={(e) => setServiceFrom(e.target.value)} disabled={!draft} />
            </label>
            <label>
              bis (optional)
              <input type="date" value={serviceTo} onChange={(e) => setServiceTo(e.target.value)} disabled={!draft} />
            </label>
          </>
        )}
        <label className="wide">
          Betreff
          <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} disabled={!draft} />
        </label>
        {draft && <TextPicker kind="subject" type={doc.type} templates={templates} value={subject} onPick={setSubject} onChanged={reloadTemplates} />}
        <label className="wide">
          Ihre Referenz oder Bestellnummer (optional)
          <input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={120} disabled={!draft} />
        </label>
        <label className="wide">
          Einleitung
          <textarea rows={3} value={intro} onChange={(e) => setIntro(e.target.value)} maxLength={4000} disabled={!draft} placeholder="Vielen Dank für Ihren Auftrag. Wir berechnen Ihnen:" />
        </label>
        {draft && <TextPicker kind="intro" type={doc.type} templates={templates} value={intro} onPick={setIntro} onChanged={reloadTemplates} />}
      </div>

      <table className="cu-table dc-lines">
        <thead>
          <tr>
            <th>Position</th>
            <th>Menge</th>
            <th>Einheit</th>
            <th>Preis netto</th>
            <th>Rabatt %</th>
            <th>Aufschlag %</th>
            <th>USt</th>
            <th>Alternativ</th>
            <th className="r">Summe</th>
            {draft && <th aria-label="Aktionen" />}
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} style={{ cursor: "default" }}>
              <td>
                <input value={l.name} onChange={(e) => setLine(i, { name: e.target.value })} disabled={!draft} aria-label={`Bezeichnung ${i + 1}`} maxLength={200} />
                <input
                  className="dc-desc"
                  value={l.description}
                  onChange={(e) => setLine(i, { description: e.target.value })}
                  disabled={!draft}
                  placeholder="Beschreibung (optional)"
                  aria-label={`Beschreibung ${i + 1}`}
                />
              </td>
              <td>
                <input className="num" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} disabled={!draft} inputMode="decimal" aria-label="Menge" />
              </td>
              <td>
                <input className="num" value={l.unit} onChange={(e) => setLine(i, { unit: e.target.value })} disabled={!draft} maxLength={20} aria-label="Einheit" />
              </td>
              <td>
                <input className="num" value={l.price} onChange={(e) => setLine(i, { price: e.target.value })} disabled={!draft} inputMode="decimal" aria-label="Preis" />
              </td>
              <td>
                <input className="num" value={l.discount} onChange={(e) => setLine(i, { discount: e.target.value })} disabled={!draft} inputMode="decimal" aria-label="Rabatt" />
              </td>
              <td>
                <input className="num" value={l.markup} onChange={(e) => setLine(i, { markup: e.target.value })} disabled={!draft} inputMode="decimal" aria-label="Aufschlag" />
                {(parseNumber(l.discount || "0") || 0) > 0 || (parseNumber(l.markup || "0") || 0) > 0 ? (
                  <label className="muted-s" style={{ display: "block", whiteSpace: "nowrap" }}>
                    <input type="checkbox" checked={l.hide} onChange={(e) => setLine(i, { hide: e.target.checked })} disabled={!draft} /> auf Beleg nicht zeigen
                  </label>
                ) : null}
              </td>
              <td>
                <select
                  value={doc.smallBusiness ? 0 : l.vatRate}
                  onChange={(e) => setLine(i, { vatRate: Number(e.target.value) as 0 | 7 | 19 })}
                  disabled={!draft || doc.smallBusiness}
                  aria-label="Umsatzsteuer"
                >
                  <option value={19}>19 %</option>
                  <option value={7}>7 %</option>
                  <option value={0}>0 %</option>
                </select>
              </td>
              <td>
                <input type="checkbox" checked={l.alt} onChange={(e) => setLine(i, { alt: e.target.checked })} disabled={!draft} aria-label={`Position ${i + 1} ist eine Alternative und zählt nicht zur Summe`} title="Alternativposition: wird angezeigt, zählt aber nicht zur Summe" />
              </td>
              <td className="r mono" style={l.alt ? { opacity: 0.6 } : undefined}>
                {parsed[i] ? (l.alt ? `(${formatCents(lineNetCents(parsed[i]!))})` : formatCents(lineNetCents(parsed[i]!))) : "–"}
              </td>
              {draft && (
                <td>
                  <button className="cu-link" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} aria-label={`Position ${i + 1} entfernen`}>
                    Entfernen
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {draft && (
        <div className="cu-actions">
          <button className="dbtn ghost small" onClick={() => addItem("")}>
            <Icon name="plus" /> Leere Position
          </button>
          <button className="dbtn ghost small" onClick={() => setCartOpen(true)}>
            <Icon name="clip" /> Warenkorb einlesen
          </button>
          {items.length > 0 && (
            <select className="dc-pick" value="" onChange={(e) => e.target.value && addItem(e.target.value)} aria-label="Leistung aus dem Stamm einfügen">
              <option value="">Leistung oder Artikel einfügen …</option>
              {items.map((it) => (
                <option key={it.id} value={it.id}>
                  {it.name} · {formatCents(it.priceCents)}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <dl className="dc-totals">
        <div>
          <dt>Netto</dt>
          <dd>{formatCents(draft ? totals.netCents : doc.netCents)}</dd>
        </div>
        {(draft ? totals.vatByRate : doc.vatByRate).map((g) =>
          doc.smallBusiness ? null : (
            <div key={g.rate}>
              <dt>Umsatzsteuer {g.rate} %</dt>
              <dd>{formatCents(g.vatCents)}</dd>
            </div>
          ),
        )}
        <div className="total">
          <dt>Gesamt</dt>
          <dd>{formatCents(draft ? totals.grossCents : doc.grossCents)}</dd>
        </div>
        {doc.paidCents > 0 && (
          <div>
            <dt>Bezahlt</dt>
            <dd>{formatCents(doc.paidCents)}</dd>
          </div>
        )}
        {doc.type === "invoice" && !draft && (
          <div>
            <dt>Noch offen</dt>
            <dd>{formatCents(doc.grossCents - doc.paidCents)}</dd>
          </div>
        )}
      </dl>
      {lines.some((l) => l.alt) && <p className="muted-s">Positionen mit Haken bei „Alternativ“ stehen auf dem Beleg als Alternative und sind nicht in der Summe enthalten.</p>}
      {doc.smallBusiness && <p className="muted-s">Als Kleinunternehmer weisen Sie keine Umsatzsteuer aus. Auf dem Beleg steht der Hinweis nach § 19 UStG.</p>}

      {doc.type !== "credit_note" && (
        <div className="cu-form wide">
          <label>
            <span>Lieferbedingung (Incoterms)</span>
            <select value={incoterm} onChange={(e) => setIncoterm(e.target.value as Incoterm | "")} disabled={!draft}>
              <option value="">Keine Angabe</option>
              {INCOTERM_KEYS.map((k) => (
                <option key={k} value={k}>
                  {INCOTERMS[k].label}
                </option>
              ))}
            </select>
            <small className="muted-s">{incoterm ? INCOTERMS[incoterm].note : "Incoterms regeln, wer Transport, Kosten und Risiko trägt. Im Inland genügt meist ein Lieferhinweis; „DAP“ passt für Lieferung an den Kunden."}</small>
          </label>
          <label>
            <span>Lieferhinweis</span>
            <input value={deliveryNote} onChange={(e) => setDeliveryNote(e.target.value)} maxLength={500} disabled={!draft} placeholder="z. B. Lieferung ca. 2 Wochen nach Auftrag, frei Baustelle" />
          </label>
          <label>
            <span>Zahlungsbedingungen</span>
            <select value="" onChange={(e) => e.target.value && setPaymentTerms(e.target.value)} disabled={!draft}>
              <option value="">Vorschlag einfügen …</option>
              {PAYMENT_TERM_PRESETS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <textarea rows={2} value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} maxLength={500} disabled={!draft} placeholder="z. B. 14 Tage netto, 2 % Skonto bei Zahlung in 8 Tagen" />
          </label>
        </div>
      )}
      {!doc.smallBusiness && (doc.type === "invoice" || doc.type === "credit_note") && lines.some((l) => !l.alt && l.vatRate === 0) && (
        <label className="cu-form wide">
          <span>Grund für 0 % Umsatzsteuer (Pflicht auf der Rechnung)</span>
          <select value={vatReason} onChange={(e) => setVatReason(e.target.value as VatReason | "")} disabled={!draft}>
            <option value="">Bitte wählen</option>
            {VAT_REASON_KEYS.map((k) => (
              <option key={k} value={k}>
                {VAT_REASONS[k].label}
              </option>
            ))}
          </select>
        </label>
      )}
      {doc.type === "invoice" && (
        <label className="cu-form wide dc-check">
          <span>
            <input type="checkbox" checked={propertyNotice} onChange={(e) => setPropertyNotice(e.target.checked)} disabled={!draft} /> Aufbewahrungshinweis für Privatpersonen auf die Rechnung setzen
          </span>
          <small className="muted-s">Pflicht bei Leistungen im Zusammenhang mit einem Grundstück (Haus, Wohnung, Garten) an Privatpersonen, § 14 Abs. 4 Nr. 9 UStG. Bei Rechnungen an Firmen abschalten.</small>
        </label>
      )}

      <label className="cu-form wide dc-closing">
        <span>Schlusstext</span>
        <textarea rows={3} value={closing} onChange={(e) => setClosing(e.target.value)} maxLength={4000} disabled={!draft} placeholder="Mit freundlichen Grüßen" />
      </label>
      {draft && <TextPicker kind="closing" type={doc.type} templates={templates} value={closing} onPick={setClosing} onChanged={reloadTemplates} />}

      <div className="dc-attach">
        <h3>
          <Icon name="clip" /> Dateien und Bilder
        </h3>
        <p className="muted-s">Zum Beispiel Warenkorb-Screenshots, Fotos oder PDFs. So lässt sich später nachvollziehen, wie der Preis zustande kam. Bei festgeschriebenen Belegen bleiben die Anhänge als Nachweis erhalten.</p>
        {doc.attachments.length > 0 && (
          <ul className="dc-log">
            {doc.attachments.map((a) => (
              <li key={a.id}>
                {a.name} · {a.sizeBytes < 1024 * 1024 ? `${Math.max(1, Math.round(a.sizeBytes / 1024))} KB` : `${(a.sizeBytes / 1024 / 1024).toFixed(1)} MB`}{" "}
                <a className="cu-link" href={`/api/documents/${doc.id}/attachments/${a.id}?ansehen=1`} target="_blank" rel="noreferrer">
                  Ansehen
                </a>{" "}
                <a className="cu-link" href={`/api/documents/${doc.id}/attachments/${a.id}`}>
                  Herunterladen
                </a>
                {draft && (
                  <>
                    {" "}
                    <button className="cu-link" onClick={() => void removeAttachment(a.id)}>
                      Entfernen
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        <input type="file" multiple accept="application/pdf,image/png,image/jpeg,image/webp" disabled={attBusy} aria-label="Dateien oder Bilder anhängen" onChange={(e) => { const f = Array.from(e.target.files ?? []); e.target.value = ""; if (f.length) void attach(f); }} />
        {attBusy && <small className="muted-s"> Lädt hoch …</small>}
        <div>
          <button type="button" className="dbtn ghost" onClick={() => setPickOpen(true)}>
            <Icon name="clip" /> Aus Projektordner wählen
          </button>
        </div>
      </div>
      {pickOpen && (
        <ProjectFilePicker
          documentId={doc.id}
          projectId={doc.projectId}
          onClose={() => setPickOpen(false)}
          onDone={(d) => setDoc((cur) => (cur ? { ...cur, attachments: d.attachments, log: d.log, attachmentCount: d.attachmentCount } : cur))}
        />
      )}
      {cartOpen && <CartImportSheet onClose={() => setCartOpen(false)} onApply={applyCart} />}

      {doc.payments.length > 0 && (
        <div>
          <h3>Zahlungen</h3>
          <ul className="dc-log">
            {doc.payments.map((p) => (
              <li key={p.id}>
                {dateText(p.paidOn)} · {formatCents(p.amountCents)}
                {p.method ? ` · ${p.method}` : ""}
                <button
                  className="cu-link"
                  onClick={() =>
                    window.confirm("Diese Zahlung entfernen? Das wird im Protokoll vermerkt.") &&
                    void run(async () => apply(await api.del<DocumentDetail>(`/api/documents/${doc.id}/payments/${p.id}`)))
                  }
                >
                  Entfernen
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {doc.dunnings.length > 0 && (
        <div>
          <h3>Mahnungen</h3>
          <ul className="dc-log">
            {doc.dunnings.map((u) => (
              <li key={u.id}>
                {DUNNING_LABEL[u.level]} vom {dateText(u.issueDate)}, zahlbar bis {dateText(u.dueDate)}
                {u.feeCents > 0 ? `, Mahngebühr ${formatCents(u.feeCents)}` : ""} ·{" "}
                <a href={`/api/documents/${doc.id}/dunnings/${u.level}/pdf`} target="_blank" rel="noreferrer">
                  PDF ansehen
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {doc.log.length > 0 && (
        <details className="dc-history">
          <summary>Protokoll und Prüfsumme</summary>
          <ul className="dc-log">
            {doc.log.map((e, i) => (
              <li key={i}>
                {new Date(e.at).toLocaleString("de-DE")} · {e.detail ?? e.action}
              </li>
            ))}
          </ul>
          {doc.hash && <p className="muted-s mono dc-hash">Prüfsumme: {doc.hash}</p>}
        </details>
      )}

      {sending && <SendSheet doc={doc} onClose={() => setSending(false)} onSent={() => api.get<DocumentDetail>(`/api/documents/${doc.id}`).then(apply).catch(() => undefined)} />}

      {paying && (
        <PaymentSheet
          doc={doc}
          onClose={() => setPaying(false)}
          onSaved={(d) => {
            apply(d);
            setPaying(false);
          }}
        />
      )}
    </section>
  );
}

function SendSheet(props: { doc: DocumentDetail; onClose: () => void; onSent: () => void }) {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [days, setDays] = useState("30");
  const [attachPdf, setAttachPdf] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<DocumentShareCreated | null>(null);
  const [copied, setCopied] = useState(false);
  const label = DOC_LABEL[props.doc.type];

  async function create(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      setCreated(await api.post<DocumentShareCreated>(`/api/documents/${props.doc.id}/send`, { email, message, days: Number(days) || 30, attachPdf }));
      props.onSent();
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  if (created) {
    const text = `${label} ${props.doc.number}: ${created.url}`;
    return (
      <Sheet title="Verschickt" onClose={props.onClose}>
        <div className="cu-form">
          <p className="wide">{created.email ? `Die E-Mail an ${created.email} ist unterwegs.` : "Der Link ist bereit."} Ihr Kunde braucht kein Konto, der Link gilt bis zum {dateText(created.expiresAt.slice(0, 10))}.</p>
          <label className="wide">
            Link zum Teilen
            <input value={created.url} readOnly onFocus={(e) => e.currentTarget.select()} />
          </label>
          <div className="cu-buttons wide">
            <button
              type="button"
              className="dbtn ghost"
              onClick={() => void navigator.clipboard?.writeText(created.url).then(() => setCopied(true))}
            >
              {copied ? "Kopiert" : "Link kopieren"}
            </button>
            <a className="dbtn ghost" href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer">
              Per WhatsApp teilen
            </a>
            <button type="button" className="dbtn" onClick={props.onClose}>
              Fertig
            </button>
          </div>
          <p className="muted-s wide">Der Link steht nur jetzt auf dem Bildschirm. Sie können jederzeit einen neuen erzeugen.</p>
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet title={`${label} verschicken`} onClose={props.onClose}>
      <form className="cu-form" onSubmit={create}>
        <div className="wide dc-preview">
          <b>So sieht Ihr Kunde das {label}:</b>
          <iframe src={`/api/documents/${props.doc.id}/pdf#toolbar=0&navpanes=0`} title={`Vorschau ${label}`} />
          <a href={`/api/documents/${props.doc.id}/pdf`} target="_blank" rel="noreferrer">
            In groß öffnen
          </a>
        </div>
        <p className="muted-s wide">
          Ihr Kunde bekommt einen Link, über den er das PDF ansieht und speichert, ohne Konto. Ohne E-Mail-Adresse erzeugen wir nur den Link, den Sie selbst weitergeben können,
          zum Beispiel per WhatsApp.
        </p>
        <label className="wide">
          E-Mail des Kunden (freiwillig)
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200} autoFocus />
        </label>
        <label className="wide">
          Nachricht (freiwillig)
          <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={1000} />
        </label>
        <label className="wide dc-check">
          <input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} /> PDF an die E-Mail anhängen
        </label>
        <label>
          Link gültig (Tage)
          <input value={days} onChange={(e) => setDays(e.target.value)} inputMode="numeric" />
        </label>
        <div className="wide">
          <Message>{error}</Message>
        </div>
        <div className="cu-buttons wide">
          <button type="button" className="dbtn ghost" onClick={props.onClose}>
            Abbrechen
          </button>
          <button className="dbtn" disabled={busy}>
            {email.trim() ? "Per E-Mail senden" : "Link erzeugen"}
          </button>
        </div>
      </form>
    </Sheet>
  );
}

function PaymentSheet(props: { doc: DocumentDetail; onClose: () => void; onSaved: (d: DocumentDetail) => void }) {
  const open = props.doc.grossCents - props.doc.paidCents;
  const [amount, setAmount] = useState(centsToText(Math.max(open, 0)));
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState("Überweisung");
  const [error, setError] = useState("");
  async function submit(e: FormEvent) {
    e.preventDefault();
    const cents = eurosToCents(amount.replace(/^-/, ""));
    if (cents === null || cents === 0) return setError("Bitte einen Betrag wie 119,00 eintragen.");
    try {
      props.onSaved(
        await api.post<DocumentDetail>(`/api/documents/${props.doc.id}/payments`, {
          amountCents: amount.trim().startsWith("-") ? -cents : cents,
          paidOn,
          method,
        }),
      );
    } catch (err) {
      setError(errorText(err));
    }
  }
  return (
    <Sheet title="Zahlung eintragen" onClose={props.onClose}>
      <form className="cu-form" onSubmit={submit}>
        <label>
          Betrag in Euro
          <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" autoFocus required />
        </label>
        <label>
          Eingegangen am
          <input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} required />
        </label>
        <label className="wide">
          Zahlungsart
          <input value={method} onChange={(e) => setMethod(e.target.value)} maxLength={60} />
        </label>
        <div className="wide">
          <Message>{error}</Message>
        </div>
        <div className="cu-buttons wide">
          <button type="button" className="dbtn ghost" onClick={props.onClose}>
            Abbrechen
          </button>
          <button className="dbtn">Eintragen</button>
        </div>
      </form>
    </Sheet>
  );
}

// ---------- Rechnungen aus einem anderen Programm ----------

function InvoiceImportSheet(props: { onClose: () => void; onDone: () => void }) {
  const [csv, setCsv] = useState<CsvInvoiceResult | null>(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<InvoiceImportResult | null>(null);

  async function readFile(file: File | undefined) {
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    try {
      setCsv(invoicesFromCsv(await file.text()));
    } catch {
      setError("Die Datei konnte nicht gelesen werden. Bitte speichern Sie sie als CSV.");
    }
  }

  async function run() {
    if (!csv) return;
    setBusy(true);
    setError("");
    try {
      setResult(await api.post<InvoiceImportResult>("/api/documents/import", { invoices: csv.invoices.map((i) => i.invoice) }));
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  return (
    <Sheet title="Rechnungen übernehmen" onClose={result ? props.onDone : props.onClose}>
      <div className="cu-form">
        <p className="muted-s wide">
          Wechseln Sie von sevDesk oder einem anderen Programm? Exportieren Sie dort Ihre Rechnungsliste als CSV-Datei (Rechnungsnummer, Datum, Kunde, Netto, Brutto,
          Status) und laden Sie sie hier hoch. Übernehmen Sie zuerst Ihre Kunden, damit die Rechnungen zugeordnet werden können. Die Rechnungen werden als
          festgeschrieben abgelegt, behalten ihre Nummer, und neue Rechnungen zählen dahinter weiter. Die Originale heben Sie bitte zusätzlich auf.
        </p>
        <input className="wide" type="file" accept=".csv,text/csv,text/plain" onChange={(e) => void readFile(e.target.files?.[0])} />
        {csv && csv.missing.length > 0 && <p className="app-msg wide">In der Datei fehlt: {csv.missing.join(", ")}. Bitte prüfen Sie die Spaltenüberschriften.</p>}
        {csv && csv.missing.length === 0 && (
          <div className="cu-preview wide">
            <p>
              <b>{fileName}</b>: {csv.invoices.length} Rechnungen erkannt
              {csv.skipped.length > 0 && `, ${csv.skipped.length} nicht lesbar`}.
            </p>
            <ul>
              {csv.invoices.slice(0, 6).map((i) => (
                <li key={i.line}>
                  {i.invoice.number} · {dateText(i.invoice.issueDate)} · {i.invoice.customerName ?? `Kunde ${i.invoice.customerNumber}`} · {formatCents(i.invoice.grossCents)}
                  {i.invoice.paid ? " · bezahlt" : ""}
                </li>
              ))}
              {csv.invoices.length > 6 && <li>… und {csv.invoices.length - 6} weitere</li>}
            </ul>
            {csv.skipped.length > 0 && <p className="muted-s">Nicht übernommen: {csv.skipped.slice(0, 5).map((s) => `Zeile ${s.line} (${s.reason})`).join(", ")}</p>}
            <button className="dbtn" disabled={busy || csv.invoices.length === 0 || !!result} onClick={() => void run()}>
              {csv.invoices.length} Rechnungen übernehmen
            </button>
          </div>
        )}
        <div className="wide">
          <Message>{error}</Message>
          {result && (
            <p className="app-msg ok">
              {result.created} Rechnungen übernommen.
              {result.skipped.length > 0 && ` Übersprungen: ${result.skipped.map((s) => `${s.number} (${s.reason})`).join(", ")}.`}
            </p>
          )}
        </div>
      </div>
    </Sheet>
  );
}

// ---------- Einstellungen: Nummern ----------

function NumberingForm() {
  const onAuth = useUnauthorizedRedirect();
  const [rows, setRows] = useState<NumberingInfo[] | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [draft, setDraft] = useState<Record<string, { prefix: string; padding: string; next: string }>>({});

  const load = useCallback(() => {
    api
      .get<NumberingInfo[]>("/api/billing/numbering")
      .then((r) => {
        setRows(r);
        setDraft(Object.fromEntries(r.map((n) => [n.type, { prefix: n.prefix, padding: String(n.padding), next: String(n.nextNumber) }])));
      })
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [onAuth]);
  useEffect(load, [load]);

  async function save(n: NumberingInfo) {
    const d = draft[n.type]!;
    setError("");
    setNotice("");
    try {
      const next = Number(d.next);
      await api.put(`/api/billing/numbering/${n.type}`, {
        prefix: d.prefix,
        padding: Number(d.padding),
        ...(next !== n.nextNumber ? { nextNumber: next } : {}),
      });
      setNotice(`${DOC_PLURAL[n.type]}: gespeichert.`);
      load();
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <section className="cu-panel">
      <p className="muted-s cu-lead">
        Die Nummern werden lückenlos vergeben. Wenn Sie von einem anderen Programm wechseln, tragen Sie hier ein, bei welcher Nummer es weitergehen soll, zum Beispiel
        „RE-“ und 4040. Die Nummer lässt sich nur erhöhen, nie senken.
      </p>
      <Message>{error}</Message>
      {notice && <p className="app-msg info">{notice}</p>}
      {rows?.map((n) => {
        const d = draft[n.type] ?? { prefix: "", padding: "4", next: "1" };
        const set = (patch: Partial<typeof d>) => setDraft((x) => ({ ...x, [n.type]: { ...d, ...patch } }));
        return (
          <div className="cu-form dc-number" key={n.type}>
            <h3 className="wide">{DOC_PLURAL[n.type]}</h3>
            <label>
              Vorsatz
              <input value={d.prefix} onChange={(e) => set({ prefix: e.target.value })} maxLength={12} />
            </label>
            <label>
              Stellen (mit Nullen)
              <input value={d.padding} onChange={(e) => set({ padding: e.target.value })} inputMode="numeric" />
            </label>
            <label>
              Nächste Nummer
              <input value={d.next} onChange={(e) => set({ next: e.target.value })} inputMode="numeric" />
            </label>
            <div className="dc-example">
              <small>Beispiel</small>
              <b className="mono">{(d.prefix || "") + String(Number(d.next) || 1).padStart(Number(d.padding) || 0, "0")}</b>
            </div>
            <div className="cu-buttons wide">
              <button className="dbtn small" onClick={() => void save(n)}>
                Speichern
              </button>
            </div>
          </div>
        );
      })}
    </section>
  );
}

// ---------- Mahnwesen ----------

function DunningView({ onOpen }: { onOpen: (id: string) => void }) {
  const onAuth = useUnauthorizedRedirect();
  const [due, setDue] = useState<DunningDueItem[] | null>(null);
  const [settings, setSettings] = useState<DunningSettings | null>(null);
  const [fees, setFees] = useState({ f1: "", f2: "", f3: "", days: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(() => {
    api
      .get<DunningDueItem[]>("/api/dunnings/due")
      .then(setDue)
      .catch((err) => onAuth(err) || setError(errorText(err)));
    api
      .get<DunningSettings>("/api/billing/dunning-settings")
      .then((s) => {
        setSettings(s);
        setFees({ f1: centsToText(s.fee1Cents), f2: centsToText(s.fee2Cents), f3: centsToText(s.fee3Cents), days: String(s.days) });
      })
      .catch(() => undefined);
  }, [onAuth]);
  useEffect(load, [load]);

  async function create(item: DunningDueItem) {
    if (!window.confirm(`${DUNNING_LABEL[item.nextLevel ?? 0]} zu ${item.number} für ${item.customerName} erstellen?`)) return;
    setError("");
    try {
      await api.post(`/api/documents/${item.documentId}/dunnings`);
      window.open(`/api/documents/${item.documentId}/dunnings/${(item.nextLevel ?? 1)}/pdf`, "_blank");
      load();
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function saveSettings(e: FormEvent) {
    e.preventDefault();
    const f1 = eurosToCents(fees.f1 || "0");
    const f2 = eurosToCents(fees.f2 || "0");
    const f3 = eurosToCents(fees.f3 || "0");
    const days = Number(fees.days);
    if (f1 === null || f2 === null || f3 === null || !Number.isInteger(days)) return setError("Bitte Beträge wie 5,00 und die Tage als ganze Zahl eintragen.");
    setError("");
    try {
      setSettings(await api.put<DunningSettings>("/api/billing/dunning-settings", { fee1Cents: f1, fee2Cents: f2, fee3Cents: f3, days }));
      setNotice("Gespeichert.");
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <section className="cu-panel">
      <Message>{error}</Message>
      {notice && <p className="app-msg info">{notice}</p>}
      <h3>Überfällige Rechnungen</h3>
      {due === null && !error && <p className="muted-s">Lädt …</p>}
      {due?.length === 0 && <p className="cu-empty">Keine Rechnung ist überfällig. Alles im grünen Bereich.</p>}
      {due && due.length > 0 && (
        <table className="cu-table">
          <thead>
            <tr>
              <th>Rechnung</th>
              <th>Kunde</th>
              <th>Überfällig</th>
              <th className="r">Offen</th>
              <th>Stand</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {due.map((x) => (
              <tr key={x.documentId} onClick={() => onOpen(x.documentId)} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen(x.documentId)}>
                <td className="mono">{x.number}</td>
                <td>{x.customerName}</td>
                <td>{x.daysOverdue} Tage (seit {dateText(x.dueDate)})</td>
                <td className="r mono">{formatCents(x.openCents)}</td>
                <td>{x.lastLevel === 0 ? "noch nicht gemahnt" : DUNNING_LABEL[x.lastLevel]}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  {x.nextLevel ? (
                    <button className="dbtn small" onClick={() => void create(x)}>
                      {DUNNING_LABEL[x.nextLevel]}
                      {x.nextFeeCents > 0 ? ` (+${formatCents(x.nextFeeCents)})` : ""}
                    </button>
                  ) : (
                    <small className="muted-s">Weiteres bitte mit Steuerberater oder Rechtsanwalt klären</small>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {settings && (
        <form className="cu-form dc-number" onSubmit={saveSettings}>
          <h3 className="wide">Mahngebühren und Frist</h3>
          <label>
            Zahlungserinnerung (Euro)
            <input value={fees.f1} onChange={(e) => setFees({ ...fees, f1: e.target.value })} inputMode="decimal" />
          </label>
          <label>
            1. Mahnung (Euro)
            <input value={fees.f2} onChange={(e) => setFees({ ...fees, f2: e.target.value })} inputMode="decimal" />
          </label>
          <label>
            2. Mahnung (Euro)
            <input value={fees.f3} onChange={(e) => setFees({ ...fees, f3: e.target.value })} inputMode="decimal" />
          </label>
          <label>
            Neue Zahlungsfrist (Tage)
            <input value={fees.days} onChange={(e) => setFees({ ...fees, days: e.target.value })} inputMode="numeric" />
          </label>
          <p className="muted-s wide">Ob und in welcher Höhe Sie Mahngebühren oder Verzugszinsen berechnen dürfen, hängt vom Vertrag ab. Bitte im Zweifel Ihren Steuerberater fragen.</p>
          <div className="cu-buttons wide">
            <button className="dbtn small">Speichern</button>
          </div>
        </form>
      )}
    </section>
  );
}
